import React from 'react';
import { X, Plus, Check } from 'lucide-react';

const EditableCriterion = ({ crit, idx, onChange, onRemove }) => (
  <div className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2">
    <span className="text-teal-400/60 text-xs w-5 flex-shrink-0">{idx + 1}.</span>
    <input
      value={crit.description}
      onChange={e => onChange(idx, 'description', e.target.value)}
      className="flex-1 bg-transparent text-white text-sm placeholder-white/20 focus:outline-none"
    />
    <div className="flex items-center gap-1 flex-shrink-0">
      <span className="text-white/30 text-xs">×</span>
      <input
        type="number" min={1} max={5}
        value={crit.weight}
        onChange={e => onChange(idx, 'weight', parseInt(e.target.value, 10) || 1)}
        className="w-10 bg-white/10 border border-white/10 rounded px-1 py-0.5 text-white text-xs text-center focus:outline-none focus:border-teal-500/50"
      />
    </div>
    <button onClick={() => onRemove(idx)} className="text-white/20 hover:text-red-400 transition-colors">
      <X className="w-3.5 h-3.5" />
    </button>
  </div>
);

export const KBReviewForm = ({
  editedName, setEditedName,
  editedDesc, setEditedDesc,
  editedThreshold, setEditedThreshold,
  editedCriteria,
  addCriterion, updateCriterion, removeCriterion,
  isDone,
}) => (
  <div className="px-6 py-4 space-y-4">
    <div className="bg-teal-500/10 border border-teal-500/20 rounded-xl p-4">
      <p className="text-teal-300 text-xs font-medium mb-1">Your Knowledge Base is ready to review</p>
      <p className="text-white/50 text-xs">Edit any criteria below before saving to your domain.</p>
    </div>

    <div>
      <label className="text-xs text-white/40 block mb-1">KB Name</label>
      <input
        value={editedName}
        onChange={e => setEditedName(e.target.value)}
        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500/50"
      />
    </div>

    <div>
      <label className="text-xs text-white/40 block mb-1">Description</label>
      <textarea
        value={editedDesc}
        onChange={e => setEditedDesc(e.target.value)}
        rows={2}
        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm resize-none focus:outline-none focus:border-teal-500/50"
      />
    </div>

    <div className="flex items-center gap-3">
      <label className="text-xs text-white/40 flex-shrink-0">Escalation Threshold</label>
      <input
        type="number" min={40} max={95}
        value={editedThreshold}
        onChange={e => setEditedThreshold(parseInt(e.target.value, 10) || 70)}
        className="w-20 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-teal-500/50"
      />
      <span className="text-white/30 text-xs">%</span>
    </div>

    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-white/50 text-xs font-medium uppercase tracking-wide">
          Grading Criteria ({editedCriteria.length})
        </p>
        <button onClick={addCriterion} className="flex items-center gap-1 text-teal-400 text-xs hover:text-teal-300">
          <Plus className="w-3.5 h-3.5" />Add
        </button>
      </div>
      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
        {editedCriteria.map((c, i) => (
          <EditableCriterion
            key={c.description?.slice(0, 20) || `crit-${i}`}
            crit={c}
            idx={i}
            onChange={updateCriterion}
            onRemove={removeCriterion}
          />
        ))}
      </div>
    </div>

    {isDone && (
      <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-3">
        <Check className="w-4 h-4 text-emerald-400" />
        <p className="text-emerald-300 text-sm">Knowledge Base saved successfully!</p>
      </div>
    )}
  </div>
);
